"use client";

import React from "react";
import { Download, FileText, FileSpreadsheet, File } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const documents = [{
  id: "doc-1",
  title: "Panduan Penggunaan Portal Resmi",
  description: "Dokumen panduan lengkap untuk menggunakan fitur-fitur portal.",
  type: "pdf",
  size: "2.4 MB",
  updatedAt: "12 Oktober 2023",
  url: "#"
}, {
  id: "doc-2",
  title: "Regulasi dan Tata Tertib Internal",
  description: "Peraturan terbaru yang berlaku untuk seluruh anggota.",
  type: "pdf",
  size: "1.1 MB",
  updatedAt: "05 November 2023",
  url: "#"
}, {
  id: "doc-3",
  title: "Formulir Pendaftaran Anggota Baru",
  description: "Silakan unduh dan isi formulir ini untuk pendaftaran.",
  type: "doc",
  size: "450 KB",
  updatedAt: "20 Januari 2024",
  url: "#"
}, {
  id: "doc-4",
  title: "Laporan Kinerja Tahunan 2023",
  description: "Ringkasan pencapaian dan laporan keuangan tahun lalu.",
  type: "pdf",
  size: "5.8 MB",
  updatedAt: "15 Februari 2024",
  url: "#"
}, {
  id: "doc-5",
  title: "Template Laporan Bulanan",
  description: "Format standar untuk pelaporan aktivitas bulanan.",
  type: "xls",
  size: "820 KB",
  updatedAt: "01 Maret 2024",
  url: "#"
}];
const getFileIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case "pdf":
      return <FileText className="size-5 text-destructive" />;
    case "xls":
    case "xlsx":
      return <FileSpreadsheet className="size-5 text-emerald-600" />;
    case "doc":
    case "docx":
      return <File className="size-5 text-blue-600" />;
    default:
      return <File className="size-5 text-muted-foreground" />;
  }
};
export default function DokumenReferensi() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="dokumen-referensi" className="w-full py-16 md:py-24 bg-background text-foreground px-4 sm:px-6 lg:px-8">
      <div ref={ref} className={`max-w-5xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mb-10 text-center md:text-left">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-foreground">
            Dokumen & Referensi
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl">
            Kumpulan dokumen penting, panduan, dan formulir yang dapat Anda unduh
            untuk keperluan referensi dan administrasi.
          </p>
        </div>

        <div className="rounded-md border border-border bg-card shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead className="w-1/2 md:w-[40%]">Nama Dokumen</TableHead>
                  <TableHead className="hidden lg:table-cell">Diperbarui</TableHead>
                  <TableHead className="hidden md:table-cell">Ukuran</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc, i) => <TableRow key={doc.id} data-index={i} className="group">
                    <TableCell className="font-medium">
                      <div className="flex items-start gap-3">
                        <div className="mt-1 shrink-0 bg-background p-2 rounded-md border border-border shadow-xs">
                          {getFileIcon(doc.type)}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-base text-foreground group-hover:text-primary transition-colors">
                            {doc.title}
                          </span>
                          <span className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                            {doc.description}
                          </span>
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground lg:hidden">
                            <span className="md:hidden">{doc.size}</span>
                            <span className="md:hidden">•</span>
                            <span>{doc.updatedAt}</span>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-muted-foreground">
                      {doc.updatedAt}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">
                      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground">
                        {doc.size}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="outline" size="sm" className="shrink-0 md:h-9 md:px-4 md:py-2">
                        <Link to={doc.url} newTab>
                          <Download className="size-4 mr-2" />
                          <span className="hidden sm:inline">Unduh</span>
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </section>;
}